// DiningMomentSheet — modal host for the dining-moment → booking flow.
//
// Mounts the existing, already-fully-wired RestaurantSearch (which itself
// chains to RestaurantCard → ReservationBooking → the real
// opentable-booking-manager backend, unchanged). This component's only job
// is to seed that search from what detectDiningMoments already figured out
// (location context, suggested radius), plus the user's learned taste
// profile (top cuisine + price tier) where available, so the traveler
// doesn't have to repeat the "where / what are you looking for" steps the
// default dialog-driven flow asks for — same isOpen/onClose mounting
// pattern as AIChatBot.
//
// Personalization note: useRestaurantSearch only reads `initialFilters` at
// its own mount time (a lazy useState initializer, not a synced prop —
// see that hook's comment), so the taste profile has to be fetched and
// resolved BEFORE RestaurantSearch mounts for it to actually take effect.
// A late-arriving profile after RestaurantSearch is already mounted
// wouldn't retroactively re-seed anything. Rather than complicate that
// hook further, this component holds RestaurantSearch's mount for the one
// taste-profile fetch (typically fast, and no slower than the search the
// user is about to do anyway).

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { colors, radii, typography, fontWeights } from '../../styles/designTokens';
import RestaurantSearch from '../RestaurantSearch';
import { getTasteProfile, getTopN } from '../../services/tasteProfileService';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const panelStyle = {
  backgroundColor: colors.surface,
  borderRadius: radii.card,
  padding: '24px',
  width: '100%',
  maxWidth: '760px',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  position: 'relative',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px',
};

const titleStyle = {
  fontSize: typography.heading.fontSize,
  fontWeight: fontWeights.bold,
  color: colors.textPrimary,
  margin: 0,
};

const closeButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  border: 'none',
  background: 'transparent',
  color: colors.textSecondary,
  cursor: 'pointer',
  borderRadius: radii.inner,
};

/**
 * Derives the RestaurantSearch `location` prop from the trip's destination
 * airport — the same "where is this trip going" signal used throughout the
 * journey layer, not a new lookup.
 */
function deriveLocation(trip) {
  const arrivalAirport = trip?.flights?.[0]?.arrivalAirport;
  return arrivalAirport ? { airportCode: arrivalAirport } : null;
}

/**
 * Maps detectDiningMoments' 4-value locationContext onto RestaurantSearch's
 * 2-value searchLocation ('airport' | 'hotel'): 'airport' stays 'airport';
 * everything else prefers 'hotel' when the trip has one booked (closest
 * available proxy for "near where they actually are"), else falls back to
 * 'airport' since `location` is always derived from the trip regardless.
 */
function deriveSearchLocation(diningMoment, trip) {
  if (diningMoment.locationContext === 'airport') return 'airport';
  const hasHotel = Array.isArray(trip?.hotels) && trip.hotels.length > 0;
  return hasHotel ? 'hotel' : 'airport';
}

/**
 * The restaurantSearch filter UI's priceOptions only ever emit '', '$', or
 * '$$' (a documented, preserved quirk — see filterOptions.js) even though
 * it shows four labeled tiers. Clamp a learned '$$$'/'$$$$' preference down
 * to '$$' so a personalized value stays representable if the user opens
 * the filter panel, instead of introducing a value the UI has no option for.
 */
function clampPriceRange(priceKey) {
  if (priceKey === '$') return '$';
  if (priceKey === '$$' || priceKey === '$$$' || priceKey === '$$$$') return '$$';
  return null;
}

/**
 * Maps the user's learned taste profile onto RestaurantSearch's filter
 * shape: top cuisine and clamped top price tier, when available. Returns
 * an empty object (no personalization) for a null/empty profile.
 */
function buildTasteProfileFilters(profile) {
  const filters = {};
  if (!profile?.dining) return filters;

  const [topCuisine] = getTopN(profile.dining.cuisines, 1);
  if (topCuisine) filters.cuisine = topCuisine;

  const [topPriceKey] = getTopN(profile.dining.priceRange, 1);
  const priceRange = clampPriceRange(topPriceKey);
  if (priceRange) filters.priceRange = priceRange;

  return filters;
}

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {Object|null} props.diningMoment - `detectDiningMoments(...)` result
 * @param {Object} props.trip
 * @param {Object} props.userProfile
 * @param {(reservation: Object) => void} [props.onReservationCreated]
 */
export function DiningMomentSheet({ isOpen, onClose, diningMoment, trip, userProfile, onReservationCreated }) {
  // `undefined` = not yet fetched this open, `null` = fetched, no profile.
  const [tasteProfile, setTasteProfile] = useState(undefined);

  useEffect(() => {
    if (!isOpen) {
      setTasteProfile(undefined);
      return undefined;
    }
    let cancelled = false;
    getTasteProfile().then((profile) => {
      if (!cancelled) setTasteProfile(profile);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen || !diningMoment) return null;

  const location = deriveLocation(trip);
  const initialSearchLocation = deriveSearchLocation(diningMoment, trip);
  const isProfileLoading = tasteProfile === undefined;
  const initialFilters = {
    radius: diningMoment.suggestedRadius,
    ...(isProfileLoading ? {} : buildTasteProfileFilters(tasteProfile)),
  };

  return (
    <div data-testid="dining-moment-sheet-overlay" style={overlayStyle} onClick={onClose}>
      <div
        data-testid="dining-moment-sheet"
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <h2 style={titleStyle}>Find a place to eat</h2>
          <button
            type="button"
            data-testid="dining-moment-sheet-close"
            onClick={onClose}
            style={closeButtonStyle}
            aria-label="Close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {isProfileLoading ? (
          <p data-testid="dining-moment-sheet-loading" style={{ color: colors.textSecondary, fontSize: typography.body.fontSize }}>
            Finding options for you…
          </p>
        ) : (
          <RestaurantSearch
            location={location}
            trip={trip}
            userProfile={userProfile}
            onReservationCreated={onReservationCreated}
            initialFilters={initialFilters}
            initialSearchLocation={initialSearchLocation}
          />
        )}
      </div>
    </div>
  );
}

export default DiningMomentSheet;
